/**
 * Fear & Greed classification — pure. NO React imports.
 *
 * Source: alternative.me's Crypto Fear & Greed Index (keyless, CORS-clean).
 * The upstream `value_classification` string is used when present, but the
 * band is recomputed locally so the colour and the number can never disagree,
 * and so the sub-section still reads correctly if that field is ever dropped.
 * Band edges are alternative.me's own published thresholds.
 */

export type FearGreedBand = 'extreme-fear' | 'fear' | 'neutral' | 'greed' | 'extreme-greed';

/** Maps to the semantic colour tokens in tailwind.config.js — never a hex. */
export type FearGreedTone = 'negative' | 'warn' | 'neutral' | 'positive';

export interface FearGreedReading {
  readonly band: FearGreedBand;
  readonly label: string;
  readonly tone: FearGreedTone;
}

const BANDS: ReadonlyArray<{ max: number; band: FearGreedBand; label: string; tone: FearGreedTone }> =
  [
    { max: 24, band: 'extreme-fear', label: 'Extreme fear', tone: 'negative' },
    { max: 44, band: 'fear', label: 'Fear', tone: 'warn' },
    { max: 55, band: 'neutral', label: 'Neutral', tone: 'neutral' },
    { max: 74, band: 'greed', label: 'Greed', tone: 'positive' },
    { max: 100, band: 'extreme-greed', label: 'Extreme greed', tone: 'positive' },
  ];

export function classifyFearGreed(value: number): FearGreedReading {
  const v = Math.min(100, Math.max(0, Math.round(value)));
  const hit = BANDS.find((b) => v <= b.max) ?? BANDS[BANDS.length - 1];
  // BANDS is a non-empty literal, so this is only unreachable defensiveness.
  return hit === undefined
    ? { band: 'neutral', label: 'Neutral', tone: 'neutral' }
    : { band: hit.band, label: hit.label, tone: hit.tone };
}

/**
 * Prefer the upstream wording when it exists, so the panel agrees with
 * alternative.me's own site; fall back to the locally derived label.
 */
export function fearGreedLabel(value: number, upstream: string): string {
  const trimmed = upstream.trim();
  return trimmed === '' ? classifyFearGreed(value).label : trimmed;
}
